import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCreateContactPerson, useUpdateContactPerson } from '@/api/domains/contact-persons';
import type { ContactPerson } from '@/api/types/pickups';
import { FormSwitchField, FormTextField } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { applyApiErrors, toApiProblem } from '@/lib/errors';

import { contactPersonSchema, type ContactPersonFormValues } from '../schemas';

const EMPTY: ContactPersonFormValues = {
  first_name: '',
  last_name: '',
  email: '',
  phone_prefix: '+48',
  phone_number: '',
  is_default: false,
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactPerson | null;
};

export function ContactPersonFormDialog({ open, onOpenChange, contact }: Props) {
  const { t } = useTranslation('settings');
  const tCommon = useTranslation('common').t;
  const create = useCreateContactPerson();
  const update = useUpdateContactPerson(contact?.id ?? 0);

  const form = useForm<ContactPersonFormValues>({
    resolver: zodResolver(contactPersonSchema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      contact
        ? {
            first_name: contact.first_name,
            last_name: contact.last_name,
            email: contact.email,
            phone_prefix: contact.phone_prefix || '+48',
            phone_number: contact.phone_number,
            is_default: contact.is_default,
          }
        : EMPTY,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact]);

  const onSubmit = (values: ContactPersonFormValues) => {
    const mutation = contact ? update : create;
    mutation.mutate(values, {
      onSuccess: () => {
        toast.success(contact ? t('contactPersons.updated') : t('contactPersons.created'));
        onOpenChange(false);
      },
      onError: (error) => applyApiErrors(form, toApiProblem(error)),
    });
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {contact ? t('contactPersons.editTitle') : t('contactPersons.addTitle')}
          </DialogTitle>
          <DialogDescription>{t('contactPersons.formDescription')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4 md:grid-cols-2">
            <FormTextField<ContactPersonFormValues>
              name="first_name"
              label={t('contactPersons.fields.firstName')}
              required
              autoComplete="given-name"
            />
            <FormTextField<ContactPersonFormValues>
              name="last_name"
              label={t('contactPersons.fields.lastName')}
              required
              autoComplete="family-name"
            />
            <FormTextField<ContactPersonFormValues>
              name="email"
              type="email"
              label={t('contactPersons.fields.email')}
              required
              className="md:col-span-2"
              autoComplete="email"
            />
            <div className="grid grid-cols-[7rem_1fr] gap-2 md:col-span-2">
              <FormTextField<ContactPersonFormValues>
                name="phone_prefix"
                label={t('contactPersons.fields.phonePrefix')}
                required
                placeholder="+48"
              />
              <FormTextField<ContactPersonFormValues>
                name="phone_number"
                type="tel"
                label={t('contactPersons.fields.phoneNumber')}
                required
                autoComplete="tel-national"
              />
            </div>
            <FormSwitchField<ContactPersonFormValues>
              name="is_default"
              label={t('contactPersons.fields.isDefault')}
              className="md:col-span-2"
            />

            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {tCommon('actions.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? tCommon('actions.saving') : tCommon('actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
